import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MapPin, Compass, LogOut, ShieldCheck, Pencil, Bell, LocateFixed, Trash2, FileText } from "lucide-react";
import { CareShell } from "@/components/CareShell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { MOBILITY_OPTIONS } from "@/lib/constants";
import { pushSupported, isPushEnabled, enablePush, disablePush } from "@/lib/push";
import { detectCareLocation } from "@/lib/careLocation";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function Field({ label, value }) {
  return (
    <div className="py-3 border-b border-[#333333] last:border-0">
      <p className="text-sm text-[#A1A1AA]">{label}</p>
      <p className="text-[#F8FAFC] font-medium">{value || "—"}</p>
    </div>
  );
}

export default function CareAccount() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [care, setCare] = useState(null);
  const [meta, setMeta] = useState({ countries: [], languages: [] });
  const [editingLoc, setEditingLoc] = useState(false);
  const [locForm, setLocForm] = useState({ country: "", city: "", preferred_language: "" });
  const [savingLoc, setSavingLoc] = useState(false);
  const [locating, setLocating] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data));
    api.get("/care/profile").then(({ data }) => setCare(data));
    api.get("/care/meta").then(({ data }) => setMeta(data));
    isPushEnabled().then(setPushOn);
  }, []);

  const startEditLoc = () => {
    setLocForm({
      country: care?.country || "",
      city: care?.city || "",
      preferred_language: care?.preferred_language || "",
    });
    setEditingLoc(true);
  };

  const saveLocation = async () => {
    if (!locForm.country) return toast.error("Please choose your country.");
    setSavingLoc(true);
    try {
      const { data } = await api.put("/care/profile", locForm);
      setCare(data);
      setEditingLoc(false);
      toast.success("Care location saved.");
    } catch {
      toast.error("Couldn't save your location. Please try again.");
    } finally {
      setSavingLoc(false);
    }
  };

  const useMyLocation = async () => {
    setLocating(true);
    try {
      const res = await detectCareLocation();
      if (!res.country && !res.city) {
        toast.error("Couldn't detect your location. Please enter it manually.");
        return;
      }
      setLocForm((f) => ({
        ...f,
        city: res.city || f.city,
        country: res.supported ? res.country : f.country,
      }));
      if (res.country && !res.supported) {
        toast.info(`We detected ${res.country}, which isn't supported yet — please pick a country from the list.`);
      } else {
        toast.success("Location detected.");
      }
    } catch (e) {
      toast.error(e.message || "Couldn't get your location.");
    } finally {
      setLocating(false);
    }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        toast.success("Opinion alerts turned off.");
      } else {
        await enablePush();
        setPushOn(true);
        toast.success("Opinion alerts are on — we'll notify you when your opinion is ready.");
      }
    } catch (e) {
      toast.error(e?.message || "Couldn't update notifications.");
    } finally {
      setPushBusy(false);
    }
  };

  const mobLabel = MOBILITY_OPTIONS.find((m) => m.value === profile?.mobility_needs)?.label;

  const doLogout = async () => {
    await logout();
    navigate("/welcome");
  };

  const deleteAll = async () => {
    try {
      await api.delete("/me");
      toast.success("All your data has been deleted.");
      await logout();
      navigate("/welcome");
    } catch {
      toast.error("Could not delete data. Please try again.");
    }
  };

  return (
    <CareShell active="account" title="Account">
      <p className="text-[#A1A1AA]">{user?.email}</p>

      {/* Care location */}
      <div className="mt-5 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5" data-testid="account-care-location">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <MapPin size={18} className="text-[#EF9F27]" />
            <h2 className="font-head text-lg font-bold text-[#F8FAFC]">Care location</h2>
          </div>
          {!editingLoc && (
            <button
              data-testid="account-edit-location-btn"
              onClick={startEditLoc}
              className="min-h-[44px] rounded-xl border border-[#EF9F27] text-[#EF9F27] px-4 flex items-center gap-1 text-sm font-bold"
            >
              <Pencil size={15} /> Edit
            </button>
          )}
        </div>

        {!editingLoc ? (
          <p className="text-[#F8FAFC]">
            {care ? `${care.city ? care.city + ", " : ""}${care.country}` : "Not set"}
            {care?.preferred_language ? ` · ${care.preferred_language}` : ""}
          </p>
        ) : (
          <div className="mt-3">
            <button
              data-testid="account-use-location-btn"
              onClick={useMyLocation}
              disabled={locating}
              className="mb-4 w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#EF9F27] text-[#EF9F27] font-bold px-4 py-3 min-h-[44px] transition-transform active:scale-[0.97] disabled:opacity-60"
            >
              <LocateFixed size={16} /> {locating ? "Detecting…" : "Use my current location"}
            </button>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-1.5">Country</label>
            <select
              data-testid="account-country-select"
              value={locForm.country}
              onChange={(e) => setLocForm({ ...locForm, country: e.target.value })}
              className="w-full bg-[#111111] border border-[#333333] rounded-xl px-4 py-3.5 min-h-[44px] text-[#F8FAFC] focus:outline-none focus:border-[#EF9F27] mb-3"
            >
              <option value="">Select country</option>
              {(meta.all_countries || []).map((c) => {
                const served = (meta.countries || []).includes(c);
                return (
                  <option key={c} value={c} disabled={!served}>
                    {served ? c : `${c} — Coming soon`}
                  </option>
                );
              })}
            </select>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-1.5">City (optional)</label>
            <input
              data-testid="account-city-input"
              value={locForm.city}
              onChange={(e) => setLocForm({ ...locForm, city: e.target.value })}
              placeholder="e.g. Accra"
              className="w-full bg-[#111111] border border-[#333333] rounded-xl px-4 py-3.5 min-h-[44px] text-[#F8FAFC] placeholder:text-[#666666] focus:outline-none focus:border-[#EF9F27] mb-3"
            />
            <label className="block text-sm font-medium text-[#A1A1AA] mb-1.5">Preferred language (optional)</label>
            <select
              data-testid="account-language-select"
              value={locForm.preferred_language}
              onChange={(e) => setLocForm({ ...locForm, preferred_language: e.target.value })}
              className="w-full bg-[#111111] border border-[#333333] rounded-xl px-4 py-3.5 min-h-[44px] text-[#F8FAFC] focus:outline-none focus:border-[#EF9F27] mb-4"
            >
              <option value="">No preference</option>
              {meta.languages.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
            <div className="flex gap-3">
              <button
                data-testid="account-cancel-location-btn"
                onClick={() => setEditingLoc(false)}
                disabled={savingLoc}
                className="flex-1 rounded-full bg-[#222222] border border-[#333333] text-[#F8FAFC] font-bold px-6 py-3.5 min-h-[48px] transition-transform active:scale-[0.97] disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                data-testid="account-save-location-btn"
                onClick={saveLocation}
                disabled={savingLoc}
                className="flex-1 rounded-full bg-[#EF9F27] text-[#111111] font-bold px-6 py-3.5 min-h-[48px] transition-transform active:scale-[0.97] disabled:opacity-60"
              >
                {savingLoc ? "Saving…" : "Save location"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Shared health profile */}
      <div className="mt-4 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Heart size={18} className="text-[#EF9F27]" />
            <h2 className="font-head text-lg font-bold text-[#F8FAFC]">Health profile</h2>
          </div>
          <button
            data-testid="account-edit-profile-btn"
            onClick={() => navigate("/profile/edit")}
            className="min-h-[44px] rounded-xl border border-[#EF9F27] text-[#EF9F27] px-4 flex items-center gap-1 text-sm font-bold"
          >
            <Pencil size={15} /> Edit
          </button>
        </div>
        <Field label="Conditions" value={(profile?.conditions || []).join(", ")} />
        <Field label="Medications" value={(profile?.medications || []).join(", ")} />
        <Field label="Allergies" value={(profile?.allergies || []).join(", ")} />
        <Field label="Mobility" value={mobLabel} />
        <Field
          label="Emergency contact"
          value={
            profile?.emergency_contact_name
              ? `${profile.emergency_contact_name} — ${profile.emergency_contact_phone}`
              : ""
          }
        />
      </div>

      <button
        data-testid="account-switch-travel-btn"
        onClick={() => navigate("/")}
        className="mt-4 w-full rounded-2xl bg-[#222222] border border-[#333333] text-[#F8FAFC] px-5 py-4 flex items-center justify-between hover:bg-[#333333] transition-colors"
      >
        <span className="flex items-center gap-2 font-semibold">
          <Compass size={18} className="text-[#EF9F27]" /> Open Lifestyle
        </span>
        <span className="text-sm text-[#A1A1AA]">Travel & wellbeing</span>
      </button>

      {/* Opinion alerts (web push) */}
      {pushSupported() && (
        <div className="mt-4 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5 flex items-center justify-between gap-3" data-testid="account-push-card">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-[#EF9F27]" />
              <h2 className="font-head text-lg font-bold text-[#F8FAFC]">Opinion alerts</h2>
            </div>
            <p className="text-sm text-[#A1A1AA] mt-1 leading-snug">
              Get a notification on this device the moment your second opinion is ready.
            </p>
          </div>
          <button
            data-testid="push-toggle-btn"
            onClick={togglePush}
            disabled={pushBusy}
            role="switch"
            aria-checked={pushOn}
            className={`relative shrink-0 w-12 h-7 rounded-full transition-colors disabled:opacity-60 ${pushOn ? "bg-[#EF9F27]" : "bg-[#444444]"}`}
          >
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${pushOn ? "translate-x-5" : ""}`} />
          </button>
        </div>
      )}

      <div className="mt-4 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={18} className="text-[#EF9F27]" />
          <h2 className="font-head text-lg font-bold text-[#F8FAFC]">Privacy</h2>
        </div>
        <p className="text-sm text-[#A1A1AA] leading-relaxed">
          Your records are only shared with a specialist when you submit a case. Second opinions are
          for discussion with your doctor and are not a diagnosis or prescription.
        </p>
      </div>

      {/* Legal */}
      <div className="mt-4 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-2" data-testid="account-legal">
        <button
          data-testid="account-privacy-link"
          onClick={() => navigate("/privacy")}
          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[#F8FAFC] hover:bg-[#222222] transition-colors"
        >
          <ShieldCheck size={18} className="text-[#EF9F27]" /> Privacy Policy
        </button>
        <button
          data-testid="account-terms-link"
          onClick={() => navigate("/terms")}
          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[#F8FAFC] hover:bg-[#222222] transition-colors"
        >
          <FileText size={18} className="text-[#EF9F27]" /> Terms of Service
        </button>
      </div>

      {/* Delete account */}
      <div className="mt-4 bg-[#1A1A1A] border border-[#4a1f1f] rounded-2xl p-5">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              data-testid="account-delete-data-btn"
              className="w-full min-h-[52px] rounded-xl bg-[#D32F2F] hover:bg-[#b71c1c] text-white font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              <Trash2 size={18} /> Delete all my data
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-w-[360px] rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete all your data?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently deletes your account and all data — health profile, documents, trips
                and reports, and any Care cases and consultations. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="min-h-[48px] rounded-xl">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="account-confirm-delete-btn"
                onClick={deleteAll}
                className="min-h-[48px] rounded-xl bg-[#D32F2F] hover:bg-[#b71c1c]"
              >
                Delete everything
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <button
        data-testid="account-logout-btn"
        onClick={doLogout}
        className="mt-4 w-full min-h-[52px] rounded-xl text-[#A1A1AA] flex items-center justify-center gap-2 hover:text-[#F8FAFC] transition-colors"
      >
        <LogOut size={18} /> Log out
      </button>
    </CareShell>
  );
}
